import React, { useMemo, useCallback, useState } from 'react'
import { ReactComponent as FiltersIcon } from '../../../../assets/images/Filters.svg'
import { ReactComponent as ContextIcon } from '../../../../assets/images/actions/context.svg'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import SvgIcon from '@mui/material/SvgIcon'
import {
    NestedMultiselectFilter,
    NestedMultiselectFilterItem,
} from '../../../../shared/components/NestedMultiselectFilter'
import { TppSection, TppRow } from '../../types'
import { useTppFiltersStorage } from '../../../../shared/hooks/useTppFiltersStorage'
import pluralize from 'pluralize'

export interface TppSectionRowFilterProps {
    sections: Record<string, TppSection>
    rows: Record<string, TppRow>
    sectionIds: string[]
}

const TppSectionRowFilter: React.FC<TppSectionRowFilterProps> = ({ sections, rows, sectionIds }) => {
    const { filters, updateFilters, updateShowContext } = useTppFiltersStorage()

    const hiddenRowIds = filters?.hiddenRowIds || []
    const showContext = !!filters?.showContext

    // Calculate all row IDs
    const allRowIds = useMemo(() => {
        return sectionIds.flatMap((sectionId) => {
            const section = sections[sectionId]
            return section?.rowIds || []
        })
    }, [sectionIds, sections])

    // Calculate selected row IDs (all rows minus hidden ones)
    const selectedRowIds = useMemo(() => {
        return allRowIds.filter((rowId) => !hiddenRowIds.includes(rowId))
    }, [allRowIds, hiddenRowIds])

    // Build items with sections as parents and rows as children
    const items: NestedMultiselectFilterItem[] = useMemo(() => {
        return sectionIds.map((sectionId) => {
            const section = sections[sectionId]
            const sectionRows = section?.rowIds || []
            const children: NestedMultiselectFilterItem[] = sectionRows.map((rowId) => {
                const row = rows[rowId]
                return {
                    id: rowId,
                    label: row?.title || rowId,
                }
            })

            return {
                id: sectionId,
                label: section?.title || sectionId,
                children: children.length > 0 ? children : undefined,
            }
        })
    }, [sectionIds, sections, rows])

    const getItemChildren = useCallback(
        (itemId: string): string[] => {
            const section = sections[itemId]
            return section?.rowIds || []
        },
        [sections]
    )

    const handleSelectionChange = useCallback(
        (newSelectedRowIds: string[]) => {
            // Calculate which rows are now hidden (all rows minus selected ones)
            const newHiddenRowIds = allRowIds.filter((id) => !newSelectedRowIds.includes(id))

            // Calculate which sections should be hidden (sections with all rows hidden)
            const newHiddenSectionIds: string[] = []
            sectionIds.forEach((sectionId) => {
                const section = sections[sectionId]
                if (!section) return

                const sectionRows = section.rowIds || []
                // Section is hidden if all its rows are hidden
                const allRowsHidden =
                    sectionRows.length > 0 && sectionRows.every((rowId) => newHiddenRowIds.includes(rowId))

                if (allRowsHidden) {
                    newHiddenSectionIds.push(sectionId)
                }
            })

            updateFilters({
                hiddenSectionIds: newHiddenSectionIds,
                hiddenRowIds: newHiddenRowIds,
            })
        },
        [allRowIds, sectionIds, sections, updateFilters]
    )

    const appliedFiltersCount = hiddenRowIds.length
    const [isFiltersTooltipOpen, setIsFiltersTooltipOpen] = useState(false)
    const [isFiltersPopoverOpen, setIsFiltersPopoverOpen] = useState(false)

    return (
        <Stack direction="row" alignItems="center" ml={-1}>
            <Tooltip
                placement="top-start"
                arrow
                open={isFiltersTooltipOpen && !isFiltersPopoverOpen}
                onOpen={() => {
                    if (!isFiltersPopoverOpen) {
                        setIsFiltersTooltipOpen(true)
                    }
                }}
                onClose={() => setIsFiltersTooltipOpen(false)}
                disableHoverListener={isFiltersPopoverOpen}
                title={
                    <Typography
                        variant={!!appliedFiltersCount ? '14x600' : '14x400'}
                        color={!!appliedFiltersCount ? 'figma.primary' : 'figma.grayscale.textHier1'}
                    >
                        {!!appliedFiltersCount
                            ? `${appliedFiltersCount} ${pluralize('filter', appliedFiltersCount)} applied`
                            : 'Apply filters'}
                    </Typography>
                }
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: 'figma.grayscale.contentWrapper',
                            px: 2,
                            py: 0.5,
                            borderRadius: '6px',
                            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                        },
                    },
                    arrow: {
                        sx: {
                            color: 'figma.grayscale.contentWrapper',
                        },
                    },
                }}
            >
                <span onClick={() => setIsFiltersTooltipOpen(false)}>
                    <NestedMultiselectFilter
                        icon={FiltersIcon}
                        items={items}
                        selectedItemIds={selectedRowIds}
                        onSelectionChange={handleSelectionChange}
                        getItemChildren={getItemChildren}
                        onOpenChange={(open) => {
                            setIsFiltersPopoverOpen(open)
                            if (open) setIsFiltersTooltipOpen(false)
                        }}
                    />
                </span>
            </Tooltip>
            <Tooltip
                placement="top-start"
                arrow
                title={
                    <Typography variant="14x400" color="figma.grayscale.textHier1">
                        {showContext ? 'Close' : 'Open'} notes
                    </Typography>
                }
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: 'figma.grayscale.contentWrapper',
                            px: 2,
                            py: 0.5,
                            borderRadius: '6px',
                            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                        },
                    },
                    arrow: {
                        sx: {
                            color: 'figma.grayscale.contentWrapper',
                        },
                    },
                }}
            >
                <IconButton
                    onClick={() => updateShowContext(!showContext)}
                    size="small"
                    sx={{
                        p: 0.5,
                        color: showContext ? 'figma.primary' : 'figma.grayscale.tier2',
                    }}
                >
                    <SvgIcon
                        component={ContextIcon}
                        inheritViewBox
                        fontSize="small"
                        color="inherit"
                        sx={{ fontSize: '20px' }}
                    />
                </IconButton>
            </Tooltip>
        </Stack>
    )
}

export default TppSectionRowFilter
